import { Avatar, type AvatarProps } from "@design-system/DataDisplay/Avatar";
import { Icon as IconBase, type IconProps } from "@design-system/Icon";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text, type TextProps } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
    createContext,
    useContext,
    type ButtonHTMLAttributes,
    type FC,
    type Ref,
} from "react";

export interface ChipProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof chipVariants> {
    asChild?: boolean;
    interactive?: boolean;
}

export const chipVariants = cva(
    "inline-flex items-center justify-center" +
        "box-border border border-secondary transition-colors " +
        "text-primary " +
        "rounded-full h-fit shadow-b-100 bg-white ",
    {
        variants: {
            interactive: {
                true:
                    "hover:bg-[#FAFAFA] hover:active:bg-[#F2F2F2] " +
                    "focus-visible:border-green focus-visible:bg-[#FFF] focus-visible:outline focus-visible:outline-1 focus-visible:outline-green " +
                    "disabled:pointer-events-none disabled:text-disabled disabled:bg-[#FAFAFA] disabled:border-tertiary " +
                    "text-sm",
                false: "cursor-default",
            },
            size: {
                sm: "px-2 py-0.5 gap-1 leading-none min-h-5 min-w-5",
                md: "px-2.5 py-1.5 gap-1.5 min-h-5 min-w-5",
            },

            active: {
                true: "bg-[#EEFFF2] hover:bg-[#EEFFF2] hover:active:bg-[#C9FAD3] focus:bg-[#EEFFF2] text-green focus:text-[#03A365] hover:text-[#028C64] hover:active:text-[#028C64]",
            },
        },
        defaultVariants: {
            size: "md",
        },
    },
);

type ChipContextType = VariantProps<typeof chipVariants>;
const chipContext = createContext<ChipContextType | null>(null);
type ChipComponentType = FC<
    ChipProps & { ref?: Ref<HTMLButtonElement | null> }
> & {
    Icon: typeof ChipIcon;
    Label: typeof ChipLabel;
    Value: typeof ChipValue;
    Avatar: typeof ChipAvatar;
};

const ChipIcon: FC<IconProps & { ref?: Ref<SVGSVGElement> }> = ({
    size,
    ref,
    ...props
}) => {
    return <IconBase {...props} size={size ?? "sm"} ref={ref} />;
};

const ChipValue: FC<TextProps & { ref?: Ref<HTMLDivElement> }> = ({
    ref,
    ...props
}) => {
    const ctx = useContext(chipContext);

    if (!ctx) {
        throw new Error("Chip.Value must be used within a Chip");
    }

    return (
        <Ellipsis
            className="max-w-[20ch]"
            asChild
            hasTooltip
            tooltipSideOffset={ctx.size === "sm" ? 5 : 10}
        >
            <Text
                variant={ctx.active ? "body-sm-bold" : "body-sm"}
                {...props}
                ref={ref}
            />
        </Ellipsis>
    );
};

const ChipLabel: FC<TextProps & { ref?: Ref<HTMLDivElement> }> = ({
    ref,
    className,
    children,
    ...props
}) => {
    const ctx = useContext(chipContext);

    if (!ctx) {
        throw new Error("Chip.Label must be used within a Chip");
    }

    const separator = ctx.active ? " : " : "";

    return (
        <Text variant="body-sm" className={cn(className)} {...props} ref={ref}>
            {children}
            {separator}
        </Text>
    );
};

const ChipAvatar: FC<AvatarProps & { ref?: Ref<HTMLDivElement> }> = ({
    className,
    ref,
    variant,
    ...props
}) => {
    return (
        <Avatar
            {...props}
            className={cn("w-4 h-4", className)}
            ref={ref}
            variant={variant ?? "circle"}
        />
    );
};

export const Chip: ChipComponentType = ({
    active: propsActive,
    className,
    size,
    asChild = false,
    children,
    interactive = true,
    ref,
    ...props
}) => {
    const active = interactive ? propsActive : false;

    const Comp = asChild ? Slot : "button";
    return (
        <chipContext.Provider value={{ active, size }}>
            <Comp
                className={cn(
                    chipVariants({
                        size,
                        active,
                        interactive,
                        className,
                    }),
                )}
                ref={ref}
                {...props}
                disabled={props.disabled}
            >
                {children}
            </Comp>
        </chipContext.Provider>
    );
};

Chip.Icon = ChipIcon;
Chip.Value = ChipValue;
Chip.Label = ChipLabel;
Chip.Avatar = ChipAvatar;
